import { Col, Radio, Row, message } from 'antd';
import { Button } from '../../../../../design-system';
import { useAppContext } from '../../../../../providers/AppContext';
import { useEffect, useRef, useState } from 'react';
import { useSelected } from '../../../../../providers/selectedContext';
import KrTable from './table';
import KrChart from './chart';
import { calculateMean } from './meanValue';
import { NoNgTableData } from '../../../../../@types/kr/types';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import NoNgTable from './noNgTable';
import { Accordion } from '../../../../../design-system/accordion';
import { CalculateSamplePayload, UpdateSamplePayload } from '../../../../../@types/samples/types';
import { calculateSample, updateSample } from '../../../../../api/data';

const EMPTY_INDEXES: number[] = [];
type ExponentSource = 'automatic' | 'refinement';

interface SavedSelection {
  no: number[];
  ng: number[];
}

interface SavedMeans {
  no: number | null;
  ng: number | null;
}

interface TableData {
  Son: number | null;
  Sgn: number | null;
  key: number;
}

const formatKrValue = (value: number | null) =>
  value == null ? '-' : value.toFixed(4);

const tableColumns = [
  {
    title: 'Son',
    dataIndex: 'Son',
    key: 'Son',
    render: (value: number | null) => formatKrValue(value)
  },
  {
    title: 'Sgn',
    dataIndex: 'Sgn',
    key: 'Sgn',
    render: (value: number | null) => formatKrValue(value)
  }
];

const GasOilKrValues = () => {
  const { updateConfigStates, data, updateData } = useAppContext();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const krAnalysis = selectedInfo?.sample?.k_rel_curves?.gas_oil;
  const inputCurves = selectedInfo?.sample?.input_sample_curves?.gas_oil;
  const [sl, setSl] = useState<{
    no: { value: number; display: boolean }[];
    ng: { value: number; display: boolean }[];
  }>({
    no: [{ value: 0, display: true }],
    ng: [{ value: 0, display: true }]
  });
  const [no, setNo] = useState<{ value: number; display: boolean }[]>([
    { value: 0, display: true }
  ]);
  const [ng, setNg] = useState<{ value: number; display: boolean }[]>([
    { value: 0, display: true }
  ]);
  const [noMean, setNoMean] = useState<number | null>();
  const [ngMean, setNgMean] = useState<number | null>();
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [noTableData, setNoTableData] = useState<NoNgTableData[]>([]);
  const [tableOption, setTableOption] = useState<string>('no');
  const [exponentSource, setExponentSource] = useState<ExponentSource>('automatic');
  const [loading, setLoading] = useState<boolean>(false);
  const [resetPending, setResetPending] = useState<boolean>(false);
  const [modeSelections, setModeSelections] = useState<
    Partial<Record<ExponentSource, SavedSelection>>
  >({});
  const [modeSavedMeans, setModeSavedMeans] = useState<
    Partial<Record<ExponentSource, SavedMeans>>
  >({});
  const lastSampleUidRef = useRef<string | undefined>();

  const [savedNoMean, setSavedNoMean] = useState<number | null>(null);
  const [savedNgMean, setSavedNgMean] = useState<number | null>(null);
  const sampleUid = selectedInfo?.sample?.uid;
  const automaticNoIndexes =
    selectedInfo?.sample?.k_rel_curves?.gas_oil?.indexes_no ??
    selectedInfo?.sample?.refinement_curves?.gas_oil?.indexes_no;
  const automaticNgIndexes =
    selectedInfo?.sample?.k_rel_curves?.gas_oil?.indexes_ng ??
    selectedInfo?.sample?.refinement_curves?.gas_oil?.indexes_ng;
  const noIndexes = automaticNoIndexes ?? EMPTY_INDEXES;
  const ngIndexes = automaticNgIndexes ?? EMPTY_INDEXES;
  const hasAutomaticNoIndexes =
    Array.isArray(automaticNoIndexes) && automaticNoIndexes.length > 0;
  const hasAutomaticNgIndexes =
    Array.isArray(automaticNgIndexes) && automaticNgIndexes.length > 0;
  const persistedRefinedNoIndexes =
    selectedInfo?.sample?.regression_indexes?.refined_no_g_selected_indexes;
  const persistedRefinedNgIndexes =
    selectedInfo?.sample?.regression_indexes?.refined_ng_selected_indexes;
  const refinedNoIndexes = persistedRefinedNoIndexes ?? EMPTY_INDEXES;
  const refinedNgIndexes = persistedRefinedNgIndexes ?? EMPTY_INDEXES;
  const hasRefinedNoIndexes =
    Array.isArray(persistedRefinedNoIndexes) && persistedRefinedNoIndexes.length > 0;
  const hasRefinedNgIndexes =
    Array.isArray(persistedRefinedNgIndexes) && persistedRefinedNgIndexes.length > 0;

  useEffect(() => {
    if (lastSampleUidRef.current === sampleUid) {
      return;
    }

    lastSampleUidRef.current = sampleUid;
    setModeSelections({});
    setModeSavedMeans({});
  }, [sampleUid]);

  const calculateRefinedExponents = () => {
    const sample = selectedInfo?.sample;
    const gasOilCurves = sample?.input_sample_curves?.gas_oil;
    const values = sample?.k_rel_values?.gas_oil;
    const sgValues = gasOilCurves?.sg_krel ?? [];
    const kroValues = gasOilCurves?.kro_lab ?? [];
    const krgValues = gasOilCurves?.krg_lab ?? [];
    const swi = sample?.input_sample_values?.swi ?? null;
    const sgc = values?.sgc ?? 0;
    const sor = values?.true_sor_g ?? values?.sor_g ?? null;
    const krgEndpoint = values?.true_krg ?? values?.krg_exponent ?? sample?.input_sample_values?.krg_absolute ?? null;

    if (swi == null || sor == null || krgEndpoint == null) {
      return {
        sl: [],
        noExponent: [],
        ngExponent: [],
      };
    }

    const denominator = 1 - swi - sgc - sor;
    if (denominator <= 0) {
      return {
        sl: [],
        noExponent: [],
        ngExponent: [],
      };
    }

    const sl = sgValues.map(value => 1 - value);
    const so = sl.map(value => value - swi);
    const son = so.map(value => (value - swi) / denominator);
    const sgn = sgValues.map(value => (value - sgc) / denominator);
    const noExponent = kroValues.map((kro, index) => {
      const sonValue = son[index];
      if (kro == null || sonValue == null || kro <= 0 || sonValue <= 0 || sonValue === 1) {
        return null;
      }
      const exponent = Math.log10(kro) / Math.log10(sonValue);
      return Number.isFinite(exponent) && exponent >= 0 ? exponent : null;
    });
    const ngExponent = krgValues.map((krg, index) => {
      const sgnValue = sgn[index];
      if (
        krg == null ||
        sgnValue == null ||
        krg <= 0 ||
        sgnValue <= 0 ||
        sgnValue === 1 ||
        krgEndpoint <= 0
      ) {
        return null;
      }
      const exponent = (Math.log10(krgEndpoint) - Math.log10(krg)) / (Math.log10(1) - Math.log10(sgnValue));
      return Number.isFinite(exponent) && exponent >= 0 ? exponent : null;
    });

    return { sl, noExponent, ngExponent };
  };

  useEffect(() => {
    if (krAnalysis?.no_exponent && krAnalysis?.ng_exponent && krAnalysis?.sl) {
      const refined = calculateRefinedExponents();
      const isRefinement = exponentSource === 'refinement';
      const slSource = isRefinement ? refined.sl : krAnalysis.sl;
      const noSource = isRefinement ? refined.noExponent : krAnalysis.no_exponent;
      const ngSource = isRefinement ? refined.ngExponent : krAnalysis.ng_exponent;
      const localSelection = modeSelections[exponentSource];
      const localSavedMeans = modeSavedMeans[exponentSource];
      const persistedNoIndexes = isRefinement ? refinedNoIndexes : noIndexes;
      const persistedNgIndexes = isRefinement ? refinedNgIndexes : ngIndexes;
      const hasPersistedNoIndexes = isRefinement
        ? hasRefinedNoIndexes
        : hasAutomaticNoIndexes;
      const hasPersistedNgIndexes = isRefinement
        ? hasRefinedNgIndexes
        : hasAutomaticNgIndexes;
      const defaultNoIndexes = localSelection?.no
        ?? (hasPersistedNoIndexes ? persistedNoIndexes : null)
        ?? noSource.map((value, index) => (value != null ? index : -1)).filter(index => index >= 0);
      const defaultNgIndexes = localSelection?.ng
        ?? (hasPersistedNgIndexes ? persistedNgIndexes : null)
        ?? ngSource.map((value, index) => (value != null ? index : -1)).filter(index => index >= 0);

      const updatedSlNo = slSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNoIndexes.includes(index)
      }));

      const updatedSlNg = slSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNgIndexes.includes(index)
      }));

      const updatedNo = noSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNoIndexes.includes(index)
      }));

      const updatedNg = ngSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNgIndexes.includes(index)
      }));

      setSl({ no: updatedSlNo, ng: updatedSlNg });
      setNo(updatedNo);
      setNg(updatedNg);

      const noMeanCalc = calculateMean(updatedNo.filter(item => item.display).map(item => item.value));
      setNoMean(noMeanCalc);

      const ngMeanCalc = calculateMean(updatedNg.filter(item => item.display).map(item => item.value));
      setNgMean(ngMeanCalc);
      setSavedNoMean(
        localSavedMeans?.no
        ?? (isRefinement
          ? selectedInfo?.sample?.k_rel_values?.gas_oil?.true_no_g ?? null
          : selectedInfo?.sample?.k_rel_values?.gas_oil?.no_g ?? null)
      );
      setSavedNgMean(
        localSavedMeans?.ng
        ?? (isRefinement
          ? selectedInfo?.sample?.k_rel_values?.gas_oil?.true_ng ?? null
          : selectedInfo?.sample?.k_rel_values?.gas_oil?.ng ?? null)
      );
    } else {
      setSl({ no: [], ng: [] });
      setNo([]);
      setNg([]);
      setNoMean(null);
      setNgMean(null);
      updateConfigStates({ showKrTable: false });
    }
  }, [
    krAnalysis,
    inputCurves,
    noIndexes,
    ngIndexes,
    refinedNoIndexes,
    refinedNgIndexes,
    hasAutomaticNoIndexes,
    hasAutomaticNgIndexes,
    hasRefinedNoIndexes,
    hasRefinedNgIndexes,
    modeSelections,
    modeSavedMeans,
    exponentSource,
    selectedInfo
  ]);

  const getTableData = () => {
    const kRelData = {
      kro_corrected:
        selectedInfo?.sample?.k_rel_curves?.gas_oil?.kro_corrected || [],
      krg_corrected:
        selectedInfo?.sample?.k_rel_curves?.gas_oil?.krg_corrected || [],
      son: selectedInfo?.sample?.k_rel_curves?.gas_oil?.son || [],
      sgn: selectedInfo?.sample?.k_rel_curves?.gas_oil?.sgn || [],
      no_exponent:
        selectedInfo?.sample?.k_rel_curves?.gas_oil?.no_exponent || [],
      ng_exponent:
        selectedInfo?.sample?.k_rel_curves?.gas_oil?.ng_exponent || []
    };

    const sg = inputCurves?.sg_krel || [];

    const currentTableData: TableData[] = [];
    for (let i = 0; i < kRelData.sgn.length; i++) {
      currentTableData.push({
        Son: kRelData.son[i],
        Sgn: kRelData.sgn[i],
        key: i
      });
    }

    const currentNoNgData: NoNgTableData[] = [];
    for (let i = 0; i < kRelData.no_exponent.length; i++) {
      currentNoNgData.push({
        sg: sg[i],
        no: kRelData.no_exponent[i],
        ng: kRelData.ng_exponent[i],
        key: i
      });
    }

    setTableData(currentTableData);
    setNoTableData(currentNoNgData);
    currentTableData.length > 0 && updateConfigStates({ showKrTable: true });
  };

  useEffect(() => {
    getTableData();
  }, [selectedInfo]);

  useEffect(() => {
    const currentNoMean = calculateMean(
      no.filter((_, index) => no[index].display).map(item => item.value)
    );
    setNoMean(currentNoMean);

    const currentNgMean = calculateMean(
      ng.filter((_, index) => ng[index].display).map(item => item.value)
    );
    setNgMean(currentNgMean);
  }, [sl, no, ng]);

  const getDisplayedIndexes = (items: { display: boolean }[]) => {
    return items.reduce((indexes, item, index) => {
      if (item.display) {
        indexes.push(index);
      }
      return indexes;
    }, [] as number[]);
  };

  const handleExponentSourceChange = (value: ExponentSource) => {
    const currentSelection = {
      no: getDisplayedIndexes(no),
      ng: getDisplayedIndexes(ng)
    };
    const currentSavedMeans = {
      no: savedNoMean,
      ng: savedNgMean
    };

    setModeSelections(prev => ({
      ...prev,
      [exponentSource]: currentSelection
    }));
    setModeSavedMeans(prev => ({
      ...prev,
      [exponentSource]: currentSavedMeans
    }));
    setExponentSource(value);
    setResetPending(false);
  };

  const handleCalculateRefinement = async () => {
    const sampleUid = selectedInfo?.sample?.uid;

    if (!sampleUid) return;
    setLoading(true);

    const indexesNo = getDisplayedIndexes(no);
    const indexesNg = getDisplayedIndexes(ng);

    try {
      let response;
      if (exponentSource === 'refinement') {
        const existingIndexes = selectedInfo?.sample?.regression_indexes || {};
        const payload: UpdateSamplePayload = {
          sample_uid: sampleUid,
          regression_indexes: resetPending
            ? {
                ...existingIndexes,
                refined_no_g_selected_indexes: [],
                refined_ng_selected_indexes: [],
              }
            : {
                ...existingIndexes,
                refined_no_g_selected_indexes: indexesNo,
                refined_ng_selected_indexes: indexesNg,
              },
          true_no_g: resetPending ? null : noMean ?? savedNoMean ?? null,
          true_ng: resetPending ? null : ngMean ?? savedNgMean ?? null,
        };
        response = await updateSample(payload);
      } else {
        const payload: CalculateSamplePayload = {
          sample_uid: sampleUid,
          strategies_keys: ['gas-oil'],
          input_data_map: {
            'gas-oil': {
              type: 'GAS_OIL',
              input_no: noMean ?? savedNoMean,
              input_ng: ngMean ?? savedNgMean,
              indexes_no: indexesNo,
              indexes_ng: indexesNg,
            },
          },
        };
        response = await calculateSample(payload);
      }
      const newWellSamplesData = data.wellSamples.map(sample => {
        if (sample.uid === response.uid) {
          return response;
        }

        return sample;
      });
      updateData({ wellSamples: newWellSamplesData });
      setSelectedInfo({ sample: response });
      setModeSelections(prev => {
        if (resetPending && exponentSource === 'refinement') {
          const nextSelections = { ...prev };
          delete nextSelections.refinement;
          return nextSelections;
        }

        return {
          ...prev,
          [exponentSource]: {
            no: indexesNo,
            ng: indexesNg
          }
        };
      });
      setModeSavedMeans(prev => {
        if (resetPending && exponentSource === 'refinement') {
          const nextSavedMeans = { ...prev };
          delete nextSavedMeans.refinement;
          return nextSavedMeans;
        }

        return {
          ...prev,
          [exponentSource]: {
            no: resetPending ? null : noMean ?? savedNoMean ?? null,
            ng: resetPending ? null : ngMean ?? savedNgMean ?? null
          }
        };
      });
      setResetPending(false);
    } catch (error) {
      console.error('error', error);
      message.error('Error saving Gas-Oil Corey exponent values.');
    }

    setLoading(false);
  };

  const handleReset = () => {
    const resetNo = no.map(item => ({ ...item, display: true }));
    const resetNg = ng.map(item => ({ ...item, display: true }));
    const resetSlNo = sl.no.map(item => ({ ...item, display: true }));
    const resetSlNg = sl.ng.map(item => ({ ...item, display: true }));
    setNo(resetNo);
    setNg(resetNg);
    setSl({ no: resetSlNo, ng: resetSlNg });
    setNoMean(calculateMean(resetNo.map(item => item.value)));
    setNgMean(calculateMean(resetNg.map(item => item.value)));
    setSavedNoMean(exponentSource === 'refinement' ? null : selectedInfo?.sample?.k_rel_values?.gas_oil?.no_g ?? null);
    setSavedNgMean(exponentSource === 'refinement' ? null : selectedInfo?.sample?.k_rel_values?.gas_oil?.ng ?? null);
    setModeSelections(prev => ({
      ...prev,
      [exponentSource]: {
        no: getDisplayedIndexes(resetNo),
        ng: getDisplayedIndexes(resetNg)
      }
    }));
    setModeSavedMeans(prev => ({
      ...prev,
      [exponentSource]: {
        no: exponentSource === 'refinement'
          ? null
          : selectedInfo?.sample?.k_rel_values?.gas_oil?.no_g ?? null,
        ng: exponentSource === 'refinement'
          ? null
          : selectedInfo?.sample?.k_rel_values?.gas_oil?.ng ?? null
      }
    }));
    setResetPending(exponentSource === 'refinement');
  };

  const handleTableOptionChange = (value: string) => {
    setTableOption(value);
  };

  return (
    <Accordion defaultOpen="kr-values-gas-oil">
      <Accordion.Item
        id="kr-values-gas-oil"
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
            <span>No &amp; Ng values</span>
            <span style={{ color: colors.gasOil }}>Gas-Oil</span>
          </div>
        }
      >
        {krAnalysis ? (
          <div style={{ padding: '0.5rem' }}>
            <Row>
              <Col span={6}>
                <KrTable
                  columns={tableColumns}
                  data={tableData}
                  bordered={true}
                />
              </Col>
              <Col span={8}>
                <div
                  style={{
                    display: 'flex',
                    marginBottom: '0.5rem',
                    marginLeft: '0.5rem',
                    justifyContent: 'space-between'
                  }}
                >
                  <Radio.Group
                    value={exponentSource}
                    onChange={e => handleExponentSourceChange(e.target.value)}
                    options={[
                      { label: 'Lab values', value: 'automatic' },
                      { label: 'Refinement', value: 'refinement' }
                    ]}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent !important',
                      marginBottom: '0.5rem'
                    }}
                    block
                    optionType="button"
                    buttonStyle="solid"
                    className={isDay ? 'day-radio' : 'night-radio'}
                  />
                  <Radio.Group
                    onChange={e => handleTableOptionChange(e.target.value)}
                    options={[
                      { label: 'No', value: 'no' },
                      { label: 'Ng', value: 'ng' }
                    ]}
                    defaultValue={'no'}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent !important'
                    }}
                    block
                    optionType="button"
                    buttonStyle="solid"
                    className={isDay ? 'day-radio' : 'night-radio'}
                  />
                </div>
                <div>
                  {tableOption === 'no' ? (
                    <NoNgTable
                      tableData={noTableData}
                      values={no}
                      setValues={setNo}
                      type="no"
                      sg={sl.no}
                      setSg={setSl}
                    />
                  ) : (
                    <NoNgTable
                      tableData={noTableData}
                      values={ng}
                      setValues={setNg}
                      type="ng"
                      sg={sl.ng}
                      setSg={setSl}
                    />
                  )}
                </div>
              </Col>
              <Col span={10}>
                <div>
                  <KrChart
                    sg={tableOption === 'no' ? sl.no : sl.ng}
                    values={tableOption === 'no' ? no : ng}
                    setValues={tableOption === 'no' ? setNo : setNg}
                    setSg={setSl}
                    mean={tableOption === 'no' ? noMean : ngMean}
                    savedMean={tableOption === 'no' ? savedNoMean : savedNgMean}
                    setSavedMean={tableOption === 'no' ? setSavedNoMean : setSavedNgMean}
                    type={tableOption === 'no' ? 'No' : 'Ng'}
                    setMean={tableOption === 'no' ? setNoMean : setNgMean}
                    color={tableOption === 'no' ? '#059e05' : '#fa3c3c'}
                  />
                </div>
                <div style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleCalculateRefinement}
                    loading={loading}
                  >
                    Save {exponentSource === 'refinement' ? 'Refined' : 'Lab'} Values
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={handleReset}
                    disabled={loading}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="no-data-div">No relative permeability values computed yet. Click <strong>Calculate</strong> in the Laboratory Data section to run the Kr analysis.</div>
        )}
      </Accordion.Item>
    </Accordion>
  );
};

export default GasOilKrValues;
